'use client';
import React from 'react';
import Link from 'next/link';
import {AppBar, Toolbar, useScrollTrigger, Skeleton} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import TopNavMenu from './TopNavMenu';
import MuiContainer from '@new-components/atoms/MuiContainer';
import ExtrasMenuList from './ExtrasMenuList';
import ResidentialsMenuList from './ResidentialsMenuList';
import DestinationsMenuList from './DestinationsMenuList';
import SideNavMenu from './SideNavMenu';
import MobNavMenuList from './MobNavMenuList';
import MobResidentialMenuList from './MobResidentialMenuList';
import MuiBox from '@new-components/atoms/MuiBox';
import MuiButton from '@new-components/atoms/MuiButton';
import MobAdvancedFilter from './MobAdvancedFilter';
import {usePathname} from 'next/navigation';
import {MediaQuery} from '@constants/theme';
import {APP_ROUTES} from '@constants/appRoutes';

export interface StickyHeader {
  /**
   * The logo to display in the header
   */
  logo: React.ReactNode;

  /**
   * Langugage displayed on the header
   */
  language: string;
  /**
   * The image to display as the flag
   */
  flagImage: string;

  /**
   * An array of static routes to display in the header
   */
  staticRoutes: {
    /**
     * The icon to display for the route
     */
    icon: React.ReactNode;
    /**
     * The label to display for the route
     */
    label: string;
    /**
     * The link to navigate to when the route is clicked
     */
    link: string;
    /**
     * Whether to hide the route on desktop devices
     */
    hideInDesktop?: boolean;
  }[];

  /**
   * The residential menu section
   */
  residentials: {
    /**
     * The label to display for the menu section
     */
    label: string;
    /**
     * An array of menu items to display in the section
     */
    items: {
      /**
       * The label to display for the menu item
       */
      label: string;
      /**
       * The link to navigate to when the menu item is clicked
       */
      link: string;
      /**
       * The description to display for the menu item
       */
      description: string;
    }[];
  };

  /**
   * The destinations menu section
   */
  destinations: {
    /**
     * The label to display for the menu section
     */
    label: string;
    /**
     * The base path for the destinations menu
     */
    basePath: string;
    /**
     * The icon to display for the menu section
     */
    icon: React.ReactNode;
    /**
     * The subtext to display for the menu section
     */
    subText?: string;
    /**
     * An array of menu items to display in the section
     */
    items: {
      /**
       * The label to display for the menu item
       */
      label: string;
      /**
       * The link to navigate to when the menu item is clicked
       */
      link: string;
      /**
       * The description to display for the menu item
       */
      description: string;
      /**
       * The image to display for the menu item
       */
      image: string;
    }[];
  };

  /**
   * The extras menu section
   */
  extras: {
    /**
     * The label to display for the menu section
     */
    label: string;
    /**
     * The subtext to display for the menu section
     */
    subText?: string;
    /**
     * An array of menu items to display in the section
     */
    items: {
      /**
       * The label to display for the menu item
       */
      label: string;
      /**
       * The link to navigate to when the menu item is clicked
       */
      link: string;
      query?: Record<string, string>;
    }[];
  };

  /**
   * The home menu section for mobile view
   */
  home: {
    /**
     * The icon to display for the route
     */
    icon: React.ReactNode;
    /**
     * The label to display for the route
     */
    label: string;
    /**
     * The link to navigate to when the route is clicked
     */
    link: string;
  };

  hasAdvancedSearch?: boolean;
}

const DropDownIcon = () => <ArrowDropDownIcon sx={{marginLeft: '-8px'}} />;

const StickyHeader: React.FC<StickyHeader> = (props: StickyHeader) => {
  const [menu, setMenu] = React.useState<'destinations' | 'extras' | ''>('');
  const [mobMenu, setMobMenu] = React.useState('');
  const pathname = usePathname();

  React.useEffect(() => {
    //close mob menu on route change
    setMobMenu('');
    setMenu('');
  }, [pathname]);

  const hideHeader = React.useMemo(
    () =>
      pathname?.includes('/teaser') ||
      pathname?.includes('/launch') ||
      pathname?.includes('/logistics-centres') ||
      pathname?.includes('/tasareeh'),
    [pathname]
  );
  const forceSolidHeader = React.useMemo(
    () =>
      pathname === '/new-home' ||
      pathname?.startsWith(APP_ROUTES.DESTINATIONS) ||
      pathname?.includes(APP_ROUTES.UNITS) ||
      pathname?.includes(APP_ROUTES.RETAIL()) ||
      pathname?.includes(APP_ROUTES.RESALE()) ||
      pathname?.includes(APP_ROUTES.AFTER_CARE()) ||
      pathname?.includes(APP_ROUTES.PREMIUM_PARKING()) ||
      (pathname?.includes(APP_ROUTES.COMMUNITIES) &&
        !pathname?.includes(`${APP_ROUTES.COMMUNITIES}/`)),
    [pathname]
  );
  const showAdvancedSearch = React.useMemo(
    () =>
      pathname?.includes('/units') &&
      !pathname?.includes('/units/'),
    [pathname]
  );

  const scrollTrigger = useScrollTrigger({
    threshold: 5,
    disableHysteresis: true,
  });
  const trigger = forceSolidHeader ? true : scrollTrigger;

  const setCurrent = (newName?: 'destinations' | 'extras') => (): void => {
    setMenu((prev) => {
      if (prev === newName) return '';
      return newName ?? '';
    });
  };
  const setMobCurrent = (newName?: string) => () =>
    setMobMenu((prev) => (prev === newName ? '' : newName ?? ''));

  const Destinations = React.useCallback(
    () => (
      <DestinationsMenuList
        transparent={!trigger}
        menus={props.destinations.items ?? []}
        onClick={setCurrent('destinations')}
      />
    ),
    [props.destinations.items, trigger]
  );
  // const residentials = React.useMemo<JSX.Element>(
  //   () => (
  //     <ResidentialsMenuList
  //       transparent={!trigger}
  //       menus={props.residentials.items}
  //       onClick={() => dispatch({type: 'RESET_MENU'})}
  //     />
  //   ),
  //   [props.residentials.items, trigger]
  // );
  const Extras = React.useCallback(
    () => (
      <ExtrasMenuList
        transparent={!trigger}
        menus={props.extras.items}
        onClick={() => setCurrent()}
      />
    ),
    [props.extras.items, trigger]
  );

  // const MobNav = React.useCallback(
  //   () => (
  //     <MobNavMenuList
  //       onClose={setMobCurrent()}
  //       basicNavItems={props.staticRoutes}
  //       destinations={props.destinations}
  //       extras={props.extras}
  //       home={props.home}
  //     />
  //   ),
  //   [props.staticRoutes, props.destinations, props.extras]
  // );
  // const mobResidentials = React.useMemo<JSX.Element>(
  //   () => (
  //     <MobResidentialMenuList
  //       residentials={props.residentials.items}
  //       onClose={setMobCurrent()}
  //     />
  //   ),
  //   [props.residentials.items]
  // );
  const TopNavComponent = React.useCallback(() => {
    switch (menu) {
      case 'destinations':
        return <Destinations />;
      case 'extras':
        return <Extras />;
      //   // case 'residentials':
      //   return residentials;
      default:
        return <></>;
    }
  }, [menu, Destinations, Extras]);

  if (hideHeader) return <></>;
  return (
    <>
      <AppBar
        position={forceSolidHeader ? 'sticky' : 'fixed'}
        sx={{
          backgroundColor: trigger || !!mobMenu ? '#2D2D2D' : 'transparent',
          borderBottom: trigger || !!mobMenu ? '1px solid #82807E1A' : 'none',
          boxShadow: 'none',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}>
        <MuiContainer
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: '32p',
            padding: '0px',
            fontSize: '13px',
            fontWeight: 500,
            lineHeight: '16.25px',
            textAlign: 'left',
            textUnderlinePosition: 'from-font',
            textDecorationSkipInk: 'none',
          }}>
          <Toolbar
            disableGutters
            sx={{
              justifyContent: 'flex-start',
              py: '12px',
              paddingInline: '0px 24px',
            }}>
            <MuiBox
              sx={{
                display: {
                  [MediaQuery.mobile]: {
                    display: 'flex',
                  },
                  display: 'none',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '4px 12px',
                  marginRight: '18px',
                  borderRight: '1px solid #82807E1A',
                },
              }}
              onClick={setMobCurrent('mobnavs')}>
              {!mobMenu ? <CloseIcon /> : <HamburgerIcon />}
            </MuiBox>
            <MuiBox
              sx={{display: 'flex', alignItems: 'center', marginRight: '36px'}}>
              {props.logo}
              {/* Feature developed based on Figma designs, but commented out as the functionality is not being implemented at this time. */}
              {/* <Button
                sx={{
                  display: {sm: 'none', xs: 'none', md: 'flex'},
                  color: '#ffffff',
                  textTransform: 'none',
                  fontSize: '14px',
                  borderRadius: '0px',
                  borderBottom:
                    state.name === 'residentials'
                      ? '2px solid #ffffff'
                      : '2px solid transparent',
                  padding: '4px 0px',
                  marginLeft: '36px',
                  ['&:hover']: {
                    borderBottom: '2px solid #ffffff',
                  },
                }}
                endIcon={<DropDownIcon />}
                onClick={setCurrent('residentials')}>
                {props.residentials.label}
              </Button> */}
              {/* Feature developed based on Figma designs, but commented out as the functionality is not being implemented at this time. */}
              {/* <Button
                sx={{
                  display: {sm: 'flex', xs: 'flex', md: 'none'},
                  color: '#ffffff',
                  textTransform: 'none',
                  fontSize: '14px',
                  borderRadius: '0px',
                  borderBottom:
                    state.name === 'residentials'
                      ? '2px solid #ffffff'
                      : '2px solid transparent',
                  marginLeft: '18px',
                  padding: '10px 0px',
                  ['&:hover']: {
                    borderBottom: '2px solid #ffffff',
                  },
                }}
                endIcon={<DropDownIcon />}
                onClick={setMobCurrent(mobResidentials)}>
                {props.residentials.label}
              </Button> */}
            </MuiBox>
            {/* Feature developed based on Figma designs, but commented out as the functionality is not being implemented at this time. */}
            {/* <Divider
              orientation='vertical'
              flexItem
              variant='middle'
              sx={{marginInline: '36px', height: '24px', marginBlock: 'auto'}}
            /> */}
            <MuiBox
              sx={{
                [MediaQuery.mobile]: {
                  display: 'none',
                },
                display: 'flex',
                alignItems: 'center',
                gap: '36px',
              }}>
              <MuiButton
                sx={{
                  color: '#ffffff',
                  textTransform: 'none',
                  fontSize: '14px',
                  borderRadius: '0px',
                  borderBottom:
                    menu === 'destinations'
                      ? '2px solid #ffffff'
                      : '2px solid transparent',
                  padding: '4px 0px',
                  ['&:hover']: {
                    borderBottom: '2px solid #ffffff',
                    background: 'transparent',
                  },
                  background: 'transparent',
                }}
                endIcon={<DropDownIcon />}
                onClick={setCurrent('destinations')}>
                {props.destinations.label}
              </MuiButton>
              {props.staticRoutes.map((route) => {
                if (route.hideInDesktop) return null;
                return (
                  <Link
                    style={{textDecoration: 'none'}}
                    href={route.link}
                    key={route.label}>
                    <MuiButton
                      sx={{
                        color: '#ffffff',
                        textTransform: 'none',
                        fontSize: '14px',
                        borderRadius: '0px',
                        borderBottom:
                          menu === route.label
                            ? '2px solid #ffffff'
                            : '2px solid transparent',
                        padding: '4px 0px',
                        ['&:hover']: {
                          borderBottom: '2px solid #ffffff',
                          background: 'transparent',
                        },
                        background: 'transparent',
                      }}>
                      {route.label}
                    </MuiButton>
                  </Link>
                );
              })}
              <MuiButton
                sx={{
                  color: '#ffffff',
                  borderRadius: '0px',
                  borderBottom:
                    menu === 'extras'
                      ? '2px solid #ffffff'
                      : '2px solid transparent',
                  ['&:hover']: {
                    borderBottom: '2px solid #ffffff',
                    background: 'transparent',
                  },
                  background: 'transparent',
                  padding: '4px 0px',
                  textTransform: 'none',
                  fontSize: '14px',
                }}
                endIcon={<DropDownIcon />}
                onClick={setCurrent('extras')}>
                {props.extras.label}
              </MuiButton>
            </MuiBox>
            {/* Feature developed based on Figma designs, but commented out as the functionality is not being implemented at this time. */}
            {/* <MuiBox
              sx={{
                ml: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}>
              <MuiTypography
                sx={{
                  display: {sm: 'none', xs: 'none', md: 'block'},
                  fontSize: '13px',
                  fontWeight: 500,
                  lineHeight: '16.25px',
                  textAlign: 'left',
                  textUnderlinePosition: 'from-font',
                  textDecorationSkipInk: 'none',
                }}>
                {props.language}
              </MuiTypography>
              <MuiBox
                sx={{
                  height: '24px',
                  width: '24px',
                  borderRadius: '100px',
                  overflow: 'hidden',
                  position: 'relative',
                }}>
                <Image
                  src={props.flagImage}
                  alt={'Language code flag'}
                  fill
                  objectFit='cover'
                />
              </MuiBox>
            </MuiBox> */}
            <React.Suspense
              fallback={
                <Skeleton
                  variant='rectangular'
                  width='42px'
                  height='32px'
                  animation='wave'
                />
              }>
              {showAdvancedSearch && <MobAdvancedFilter />}
            </React.Suspense>
          </Toolbar>
        </MuiContainer>
      </AppBar>
      <TopNavMenu
        open={!!menu}
        transparent={!trigger}
        onClose={setCurrent(undefined)}>
        <MuiContainer>
          <TopNavComponent />
        </MuiContainer>
      </TopNavMenu>
      <SideNavMenu open={!!mobMenu} onClose={setMobCurrent(undefined)}>
        <MobNavMenuList
          onClose={setMobCurrent()}
          basicNavItems={props.staticRoutes}
          destinations={props.destinations}
          extras={props.extras}
          home={props.home}
        />
      </SideNavMenu>
    </>
  );
};

export default StickyHeader;

function CloseIcon() {
  return (
    <svg
      width='24'
      height='24'
      viewBox='0 0 24 24'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M4 5L20 5'
        stroke='white'
        strokeWidth='1.25'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M4 12L20 12'
        stroke='white'
        strokeWidth='1.25'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M4 19L20 19'
        stroke='white'
        strokeWidth='1.25'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}
function HamburgerIcon() {
  return (
    <svg
      width='24'
      height='24'
      viewBox='0 0 24 24'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M19.0005 5L5.00049 19M5.00049 5L19.0005 19'
        stroke='white'
        strokeWidth='1.25'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}
